import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router-dom';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { FormFieldValue } from '../forms/FormFieldValue';
import { GET_BATCH_BY_ID_QUERY, GET_BATCHES_QUERY } from '../../queries/BatchQueries';
import { UPDATE_BATCH_MUTATION } from '../../mutations/BatchMutations';
import { type Batch, validate as validateBatch } from '../../models/Batch';
import BatchForm, { BatchFormState } from './BatchForm';

function BatchEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<BatchFormState['values']>;
  onSubmit: (id: number, formValues: Partial<BatchFormState['values']>) => Promise<boolean>;
}) {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [formState, setFormState] = useState<BatchFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<BatchFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<BatchFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof BatchFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<BatchFormState['values']>) => {
        const { issues } = validateBatch(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateBatch(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    const id = +(batchId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(id, formValues);

        if (success) {
          notifications.show('Batch edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/batches/${id}`);
        } else {
          notifications.show(
            'Failed to edit batch. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit batch. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [batchId, formValues, navigate, notifications, onSubmit, setFormErrors]);

  return (
    <BatchForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/batches/${batchId}`}
    />
  );
}

export default function BatchEdit() {
  const { batchId } = useParams();

  const [getBatchById] = useLazyQuery(
    GET_BATCH_BY_ID_QUERY);

  const [updateBatch] = useMutation(
    UPDATE_BATCH_MUTATION, {
    refetchQueries: [{ query: GET_BATCHES_QUERY }, { query: GET_BATCH_BY_ID_QUERY, variables: { id: +(batchId || '0') } }],
  });

  const [batch, setBatch] = useState<Batch | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(batchId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getItemIdError } = await getBatchById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getItemIdError) {
          setError(getItemIdError);
        }

        if (data?.batch) {
          setBatch(data.batch);
        } else {
          setError({ message: 'No batch found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [batchId, getBatchById]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<BatchFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateBatch({
        variables: {
          id: id,
          updatedAmount: formValues.amount || 0
        }
      });

      setIsLoading(false);

      if (result?.batch) {
        setBatch(result.batch);
        return true;
      }

      return false;
    },
    [updateBatch],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return batch ? (
      <BatchEditForm
        initialValues={batch}
        onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, batch, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${batch?.product?.name} Batch`}
      breadcrumbs={[
        { title: 'Batches', path: '/dashboard/batches' },
        { title: `${batch?.product?.name}`, path: `/dashboard/batches/${batchId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}